"use client";

import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { createPortal } from "react-dom";
import {
  Bell,
  X,
  Building2,
  Briefcase,
  PhoneCall,
  Monitor,
  Globe,
  Layers,
  Clock,
  RefreshCw,
  Loader2,
  UserPlus,
  PlayCircle,
  Package,
  Sparkles,
  ChevronDown,
} from "lucide-react";
import { apiService } from "../../services/Service";

// ─── Types ────────────────────────────────────────────────────────────────────

type NotificationCategory =
  | "user_created"
  | "assessment"
  | "sales_request"
  | "demo_request"
  | "job"
  | "agency_job"
  | "career_job";

interface Notification {
  _id: string;
  createdAt: string;
  category: NotificationCategory;
  isRead: boolean;
  data: Record<string, any>;
}

// ─── Category config ──────────────────────────────────────────────────────────

const CATEGORY_CONFIG: Record<
  NotificationCategory,
  {
    icon: React.ElementType;
    label: string;
    gradient: string;
    dot: string;
    pill: string;
    pillText: string;
  }
> = {
  user_created: {
    icon: UserPlus,
    label: "User",
    gradient: "from-violet-500 to-purple-600",
    dot: "bg-violet-500",
    pill: "bg-violet-50 dark:bg-violet-900/30 border-violet-200 dark:border-violet-700",
    pillText: "text-violet-700 dark:text-violet-300",
  },
  assessment: {
    icon: Monitor,
    label: "Assessment",
    gradient: "from-blue-500 to-indigo-600",
    dot: "bg-blue-500",
    pill: "bg-blue-50 dark:bg-blue-900/30 border-blue-200 dark:border-blue-700",
    pillText: "text-blue-700 dark:text-blue-300",
  },
  sales_request: {
    icon: PhoneCall,
    label: "Sales",
    gradient: "from-emerald-500 to-teal-600",
    dot: "bg-emerald-500",
    pill: "bg-emerald-50 dark:bg-emerald-900/30 border-emerald-200 dark:border-emerald-700",
    pillText: "text-emerald-700 dark:text-emerald-300",
  },
  demo_request: {
    icon: PlayCircle,
    label: "Demo",
    gradient: "from-amber-500 to-orange-500",
    dot: "bg-amber-500",
    pill: "bg-amber-50 dark:bg-amber-900/30 border-amber-200 dark:border-amber-700",
    pillText: "text-amber-700 dark:text-amber-300",
  },
  job: {
    icon: Briefcase,
    label: "Job",
    gradient: "from-rose-500 to-pink-600",
    dot: "bg-rose-500",
    pill: "bg-rose-50 dark:bg-rose-900/30 border-rose-200 dark:border-rose-700",
    pillText: "text-rose-700 dark:text-rose-300",
  },
  agency_job: {
    icon: Layers,
    label: "Agency",
    gradient: "from-orange-500 to-red-500",
    dot: "bg-orange-500",
    pill: "bg-orange-50 dark:bg-orange-900/30 border-orange-200 dark:border-orange-700",
    pillText: "text-orange-700 dark:text-orange-300",
  },
  career_job: {
    icon: Globe,
    label: "Career",
    gradient: "from-teal-500 to-cyan-600",
    dot: "bg-teal-500",
    pill: "bg-teal-50 dark:bg-teal-900/30 border-teal-200 dark:border-teal-700",
    pillText: "text-teal-700 dark:text-teal-300",
  },
};

// ─── Time formatter ───────────────────────────────────────────────────────────

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d`;
  return new Date(dateStr).toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
  });
}

// ─── Body Renderers ───────────────────────────────────────────────────────────

function UserCreatedBody({ data }: { data: Record<string, any> }) {
  const { subjectLabel, subjectName, subjectEmail, creatorLabel, creatorName, planInfo, institutionName } = data;
  return (
    <div className="space-y-1.5">
      <p className="text-[13px] font-semibold text-gray-900 dark:text-white leading-snug">
        New <span className="text-violet-600 dark:text-violet-400">{subjectLabel}</span> registered
      </p>
      <p className="text-[12px] text-gray-500 dark:text-gray-400 leading-relaxed">
        <span className="font-medium text-gray-700 dark:text-gray-200">{subjectName}</span>
        <span className="opacity-60"> · {subjectEmail}</span>
      </p>
      {creatorName && (
        <p className="text-[11px] text-gray-400 dark:text-gray-500">
          Added by <span className="text-[#007A5F] font-medium">{creatorLabel}: {creatorName}</span>
        </p>
      )}
      <div className="flex flex-wrap gap-1 pt-0.5">
        {institutionName && (
          <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 rounded-full border border-gray-200 dark:border-gray-700">
            <Building2 size={8} /> {institutionName}
          </span>
        )}
        {planInfo && (
          <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 bg-violet-50 dark:bg-violet-900/30 text-violet-700 dark:text-violet-300 rounded-full border border-violet-200 dark:border-violet-700">
            <Package size={8} /> {planInfo.planName}
          </span>
        )}
      </div>
    </div>
  );
}

function AssessmentBody({ data }: { data: Record<string, any> }) {
  const { name, status, testLiveFrom, testLiveTo, isProctored, target, createdByName } = data;
  const statusStyles: Record<string, string> = {
    draft: "bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400",
    scheduled: "bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300",
    launched: "bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300",
    completed: "bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300",
  };
  return (
    <div className="space-y-1.5">
      <p className="text-[13px] font-semibold text-gray-900 dark:text-white leading-snug line-clamp-1">{name}</p>
      <div className="flex flex-wrap items-center gap-1.5">
        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wide ${statusStyles[status] || statusStyles.draft}`}>{status}</span>
        {isProctored && (
          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-300 uppercase tracking-wide">Proctored</span>
        )}
        <span className="text-[10px] text-gray-400 flex items-center gap-1"><Layers size={9} />{target}</span>
      </div>
      <p className="text-[11px] text-gray-400 dark:text-gray-500 flex items-center gap-1">
        <Clock size={9} />
        {new Date(testLiveFrom).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}
        {" → "}
        {new Date(testLiveTo).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
        <span className="opacity-40">·</span> {createdByName}
      </p>
    </div>
  );
}

function SalesRequestBody({ data }: { data: Record<string, any> }) {
  const { name, institution, email, priority } = data;
  const pStyles: Record<string, string> = {
    high: "bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-300",
    medium: "bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-300",
    low: "bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400",
  };
  return (
    <div className="space-y-1.5">
      <p className="text-[13px] font-semibold text-gray-900 dark:text-white">
        Sales inquiry — <span className="text-emerald-600 dark:text-emerald-400">{name}</span>
      </p>
      <p className="text-[11px] text-gray-500 dark:text-gray-400">
        <span className="font-medium text-gray-600 dark:text-gray-300">{institution}</span>
        <span className="opacity-50"> · </span>{email}
      </p>
      <span className={`inline-block text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wide ${pStyles[priority] || pStyles.medium}`}>
        {priority} priority
      </span>
    </div>
  );
}

function DemoRequestBody({ data }: { data: Record<string, any> }) {
  const { name, type, institution, email } = data;
  return (
    <div className="space-y-1.5">
      <p className="text-[13px] font-semibold text-gray-900 dark:text-white">
        Demo request — <span className="text-amber-600 dark:text-amber-400">{name}</span>
      </p>
      <p className="text-[11px] text-gray-500 dark:text-gray-400">
        <span className="font-medium text-gray-600 dark:text-gray-300">{institution}</span>
        <span className="opacity-50"> · </span>{email}
      </p>
      <span className="inline-block text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300 uppercase tracking-wide">
        {type}
      </span>
    </div>
  );
}

function JobBody({ data }: { data: Record<string, any> }) {
  const { title, type, location, status, companyName } = data;
  return (
    <div className="space-y-1.5">
      <p className="text-[13px] font-semibold text-gray-900 dark:text-white line-clamp-1">{title}</p>
      <p className="text-[11px] text-gray-500 dark:text-gray-400 flex items-center gap-1">
        <Building2 size={9} />
        <span className="font-medium text-gray-600 dark:text-gray-300">{companyName}</span>
        <span className="opacity-50">·</span>{location}
      </p>
      <div className="flex gap-1.5">
        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-rose-50 dark:bg-rose-900/20 text-rose-600 dark:text-rose-300 uppercase tracking-wide">{type}</span>
        {status === "active" && (
          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300 uppercase tracking-wide">Active</span>
        )}
      </div>
    </div>
  );
}

function AgencyJobBody({ data }: { data: Record<string, any> }) {
  const { title, employmentType, location, agencyName } = data;
  return (
    <div className="space-y-1.5">
      <p className="text-[13px] font-semibold text-gray-900 dark:text-white line-clamp-1">{title}</p>
      <p className="text-[11px] text-gray-500 dark:text-gray-400 flex items-center gap-1">
        <Briefcase size={9} />
        <span className="font-medium text-gray-600 dark:text-gray-300">{agencyName}</span>
        <span className="opacity-50">·</span>{location}
      </p>
      <span className="inline-block text-[10px] font-bold px-2 py-0.5 rounded-full bg-orange-50 dark:bg-orange-900/20 text-orange-600 dark:text-orange-300 uppercase tracking-wide">{employmentType}</span>
    </div>
  );
}

function CareerJobBody({ data }: { data: Record<string, any> }) {
  const { title, department, type, location, postedByName } = data;
  return (
    <div className="space-y-1.5">
      <p className="text-[13px] font-semibold text-gray-900 dark:text-white line-clamp-1">{title}</p>
      <p className="text-[11px] text-gray-500 dark:text-gray-400 flex items-center gap-1">
        <Globe size={9} />
        <span className="font-medium text-gray-600 dark:text-gray-300">{department}</span>
        <span className="opacity-50">·</span>{location}
      </p>
      <p className="text-[11px] text-gray-400 dark:text-gray-500">
        {type} · Posted by <span className="text-gray-500 dark:text-gray-400">{postedByName}</span>
      </p>
    </div>
  );
}

// ─── Notification Card ────────────────────────────────────────────────────────

function NotificationCard({ notification, index }: { notification: Notification; index: number }) {
  const cfg = CATEGORY_CONFIG[notification.category];
  const Icon = cfg.icon;

  const bodyMap: Record<NotificationCategory, React.ReactNode> = {
    user_created: <UserCreatedBody data={notification.data} />,
    assessment: <AssessmentBody data={notification.data} />,
    sales_request: <SalesRequestBody data={notification.data} />,
    demo_request: <DemoRequestBody data={notification.data} />,
    job: <JobBody data={notification.data} />,
    agency_job: <AgencyJobBody data={notification.data} />,
    career_job: <CareerJobBody data={notification.data} />,
  };

  return (
    <div
      className={`
        group relative flex gap-3 px-4 py-3.5
        border-b border-gray-100 dark:border-gray-800/80
        transition-all duration-200
        hover:bg-gray-50 dark:hover:bg-gray-800/50
        ${!notification.isRead ? "bg-white dark:bg-gray-900" : "bg-transparent"}
      `}
      style={{ animationDelay: `${index * 30}ms` }}
    >
      {/* Unread indicator line on left */}
      {!notification.isRead && (
        <div className={`absolute left-0 top-2 bottom-2 w-[3px] rounded-r-full bg-gradient-to-b ${cfg.gradient}`} />
      )}

      {/* Icon */}
      <div className={`
        shrink-0 w-9 h-9 rounded-xl flex items-center justify-center
        bg-gradient-to-br ${cfg.gradient} shadow-sm
        group-hover:scale-105 transition-transform duration-200
      `}>
        <Icon size={15} className="text-white" strokeWidth={2} />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        {bodyMap[notification.category]}
      </div>

      {/* Time + unread dot */}
      <div className="shrink-0 flex flex-col items-end gap-1.5 mt-0.5">
        <span className="text-[10px] text-gray-400 dark:text-gray-500 whitespace-nowrap font-medium">
          {timeAgo(notification.createdAt)}
        </span>
        {!notification.isRead && (
          <span className={`w-2 h-2 rounded-full ${cfg.dot} ring-2 ring-white dark:ring-gray-900`} />
        )}
      </div>
    </div>
  );
}

// ─── Filter Tabs ──────────────────────────────────────────────────────────────

const FILTER_TABS: { key: NotificationCategory | "all"; label: string; emoji: string }[] = [
  { key: "all", label: "All", emoji: "✦" },
  { key: "user_created", label: "Users", emoji: "👤" },
  { key: "assessment", label: "Tests", emoji: "🖥" },
  { key: "sales_request", label: "Sales", emoji: "📞" },
  { key: "demo_request", label: "Demos", emoji: "▶" },
  { key: "job", label: "Jobs", emoji: "💼" },
  { key: "agency_job", label: "Agency", emoji: "⚡" },
  { key: "career_job", label: "Careers", emoji: "🌐" },
];

// ─── Bell Icon ────────────────────────────────────────────────────────────────

function BellIcon({ hasUnread, count, isOpen }: { hasUnread: boolean; count: number; isOpen: boolean }) {
  return (
    <div className="relative">
      {/* Outer glow ring when has unread */}
      {hasUnread && !isOpen && (
        <span className="absolute inset-0 rounded-xl bg-[#007A5F]/20 animate-ping" style={{ animationDuration: "2s" }} />
      )}

      <div className={`
        relative w-9 h-9 rounded-xl flex items-center justify-center
        transition-all duration-300
        ${isOpen
          ? "bg-gradient-to-br from-[#007A5F] to-[#005a46] shadow-lg shadow-[#007A5F]/30"
          : hasUnread
          ? "bg-gradient-to-br from-[#007A5F]/15 to-[#007A5F]/5 border border-[#007A5F]/30"
          : "bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700"
        }
      `}>
        <Bell
          size={16}
          className={`transition-all duration-300 ${
            isOpen ? "text-white" : hasUnread ? "text-[#007A5F]" : "text-gray-500 dark:text-gray-400"
          }`}
          strokeWidth={isOpen ? 2.5 : 2}
        />

        {/* Badge */}
        {count > 0 && (
          <span className="
            absolute -top-1.5 -right-1.5
            min-w-[18px] h-[18px] px-1
            bg-gradient-to-br from-red-500 to-rose-600
            text-white text-[9px] font-black
            rounded-full flex items-center justify-center
            border-2 border-white dark:border-gray-900
            shadow-sm
          ">
            {count > 99 ? "99+" : count}
          </span>
        )}
      </div>
    </div>
  );
}

// ─── Empty State ──────────────────────────────────────────────────────────────

function EmptyState({ onReset, showReset }: { onReset: () => void; showReset: boolean }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 gap-4">
      <div className="relative">
        <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-800 dark:to-gray-700 flex items-center justify-center shadow-inner">
          <Bell size={28} className="text-gray-300 dark:text-gray-600" />
        </div>
        <div className="absolute -top-1 -right-1 w-6 h-6 rounded-lg bg-gradient-to-br from-[#007A5F] to-[#005a46] flex items-center justify-center">
          <Sparkles size={12} className="text-white" />
        </div>
      </div>
      <div className="text-center space-y-1">
        <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">All clear!</p>
        <p className="text-xs text-gray-400 dark:text-gray-500">No notifications in this category</p>
      </div>
      {showReset && (
        <button
          onClick={onReset}
          className="text-xs text-[#007A5F] font-semibold hover:underline flex items-center gap-1"
        >
          Show all notifications
        </button>
      )}
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

interface AdminNotificationPanelProps {
  userRole?: string;
}

export function AdminNotificationPanel({ userRole }: AdminNotificationPanelProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [activeFilter, setActiveFilter] = useState<NotificationCategory | "all">("all");
  const [refreshing, setRefreshing] = useState(false);

  const bellRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [panelPos, setPanelPos] = useState({ top: 0, left: 0 });

  // ── Position panel ───────────────────────────────────────────────────────────
  const updatePos = useCallback(() => {
    if (!bellRef.current) return;
    const rect = bellRef.current.getBoundingClientRect();
    const panelW = 420;
    const vw = window.innerWidth;
    let left = rect.right + 10;
    if (left + panelW > vw - 12) left = rect.left - panelW - 10;
    setPanelPos({ top: rect.top, left });
  }, []);

  // ── Fetch unread count ───────────────────────────────────────────────────────
  const fetchUnreadCount = useCallback(async () => {
    try {
      const res: any = await apiService.get("/admin-notifications/unread-count");
      if (res?.success) setUnreadCount(Math.min(res.count, 99));
    } catch {}
  }, []);

// Derive once, outside the effect
const isAdmin = userRole?.toLowerCase() === "admin";

useEffect(() => {
  console.log("userRole: ", userRole)
  if (!isAdmin) return;          // ← early exit, no fetch, no interval

  fetchUnreadCount();
  const interval = setInterval(fetchUnreadCount, 60_000);
  return () => clearInterval(interval);
}, [fetchUnreadCount, isAdmin]);

  // ── Fetch notifications ──────────────────────────────────────────────────────
  const fetchNotifications = useCallback(
    async (replace = true) => {
      if (replace) setLoading(true);
      else setLoadingMore(true);

      try {
        const cursor = replace ? new Date().toISOString() : nextCursor;
        const categoryParam = activeFilter !== "all" ? `&category=${activeFilter}` : "";
        const res: any = await apiService.get(
          `/admin-notifications?limit=20${cursor ? `&cursor=${cursor}` : ""}${categoryParam}`
        );
        if (res?.success) {
          const items: Notification[] = res.notifications;
          setNotifications((prev) => (replace ? items : [...prev, ...items]));
          setNextCursor(res.nextCursor);
          setHasMore(!!res.nextCursor);

          // FIX: update unread count from fetched data directly (no poll delay)
          if (replace) {
            const unread = items.filter((n) => !n.isRead).length;
            setUnreadCount((prev) => (unread > prev ? unread : prev));
          }
        }
      } catch {
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [nextCursor, activeFilter]
  );

  // ── Re-fetch when tab changes ────────────────────────────────────────────────
  useEffect(() => {
    if (!isOpen) return;
    fetchNotifications(true);
  }, [activeFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Open panel ───────────────────────────────────────────────────────────────
  const handleOpen = useCallback(async () => {
    updatePos();
    setIsOpen(true);
    fetchNotifications(true);

    setTimeout(async () => {
      try {
        await apiService.post("/admin-notifications/mark-read", {});
        setUnreadCount(0);
        setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
      } catch {}
    }, 1200);
  }, [fetchNotifications, updatePos]);

  // ── Refresh ──────────────────────────────────────────────────────────────────
  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    await fetchNotifications(true);
    setRefreshing(false);
  }, [fetchNotifications]);

  // ── Infinite scroll ──────────────────────────────────────────────────────────
  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el || loadingMore || !hasMore) return;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 120) {
      fetchNotifications(false);
    }
  }, [loadingMore, hasMore, fetchNotifications]);

  // ── Outside click / Escape ───────────────────────────────────────────────────
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: MouseEvent) => {
      if (panelRef.current?.contains(e.target as Node) || bellRef.current?.contains(e.target as Node)) return;
      setIsOpen(false);
    };
    const escHandler = (e: KeyboardEvent) => e.key === "Escape" && setIsOpen(false);
    document.addEventListener("mousedown", handler);
    document.addEventListener("keydown", escHandler);
    return () => {
      document.removeEventListener("mousedown", handler);
      document.removeEventListener("keydown", escHandler);
    };
  }, [isOpen]);

  // ── Reposition ───────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!isOpen) return;
    window.addEventListener("resize", updatePos);
    window.addEventListener("scroll", updatePos, true);
    return () => {
      window.removeEventListener("resize", updatePos);
      window.removeEventListener("scroll", updatePos, true);
    };
  }, [isOpen, updatePos]);

  const totalUnread = useMemo(() => notifications.filter((n) => !n.isRead).length, [notifications]);

  if (userRole?.toLowerCase() !== "admin") return null;

  return (
    <>
      {/* ── Bell Button ── */}
      <button
        ref={bellRef}
        onClick={isOpen ? () => setIsOpen(false) : handleOpen}
        className="relative flex items-center justify-center outline-none focus-visible:ring-2 focus-visible:ring-[#007A5F] focus-visible:ring-offset-2 rounded-xl"
        title="Notifications"
      >
        <BellIcon hasUnread={unreadCount > 0} count={unreadCount} isOpen={isOpen} />
      </button>

      {/* ── Floating Panel ── */}
      {isOpen &&
        createPortal(
          <div
            ref={panelRef}
            style={{
              position: "fixed",
              top: panelPos.top,
              left: panelPos.left,
              width: 500,
              zIndex: 99999,
              maxHeight: "calc(92vh - 20px)",
            }}
            className="
              flex flex-col
              bg-white dark:bg-[#111318]
              rounded-2xl
              border border-gray-200/80 dark:border-gray-700/60
              shadow-[0_24px_80px_-12px_rgba(0,0,0,0.18),0_0_0_1px_rgba(0,0,0,0.04)]
              dark:shadow-[0_24px_80px_-12px_rgba(0,0,0,0.6),0_0_0_1px_rgba(255,255,255,0.04)]
              overflow-hidden
              animate-in fade-in zoom-in-95 slide-in-from-top-1 duration-200
              origin-top-left
            "
          >
            {/* ── Header ── */}
            <div className="relative px-4 pt-4 pb-3 shrink-0 border-b border-gray-100 dark:border-gray-800/80">
              {/* Subtle top gradient accent */}
              <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-[#007A5F] via-emerald-400 to-teal-500 rounded-t-2xl" />

              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  {/* Header icon */}
                  <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#007A5F] to-[#005a46] flex items-center justify-center shadow-md shadow-[#007A5F]/20">
                    <Bell size={15} className="text-white" strokeWidth={2.5} />
                  </div>
                  <div>
                    <h3 className="text-[15px] font-bold text-gray-900 dark:text-white tracking-tight">
                      Notifications
                    </h3>
                    <p className="text-[11px] text-gray-400 dark:text-gray-500 font-medium">
                      {totalUnread > 0
                        ? `${totalUnread} unread · Activity feed`
                        : "All caught up · Activity feed"}
                    </p>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-1">
                  <button
                    onClick={handleRefresh}
                    disabled={refreshing || loading}
                    className="w-8 h-8 rounded-lg flex items-center justify-center text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-all disabled:opacity-40"
                    title="Refresh"
                  >
                    <RefreshCw size={13} className={refreshing ? "animate-spin" : ""} />
                  </button>
                  <button
                    onClick={() => setIsOpen(false)}
                    className="w-8 h-8 rounded-lg flex items-center justify-center text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-all"
                  >
                    <X size={13} />
                  </button>
                </div>
              </div>

              {/* Stats row */}
              {/* {notifications.length > 0 && (
                <div className="flex items-center gap-2 mt-3">
                  {Object.entries(CATEGORY_CONFIG).map(([cat, cfg]) => {
                    const count = notifications.filter((n) => n.category === cat).length;
                    if (count === 0) return null;
                    const Icon = cfg.icon;
                    return (
                      <button
                        key={cat}
                        onClick={() => setActiveFilter(cat as NotificationCategory)}
                        className={`
                          flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] font-bold
                          transition-all duration-150
                          ${activeFilter === cat
                            ? `bg-gradient-to-br ${cfg.gradient} text-white shadow-sm`
                            : "bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700"
                          }
                        `}
                      >
                        <Icon size={9} />
                        {count}
                      </button>
                    );
                  })}
                  {activeFilter !== "all" && (
                    <button
                      onClick={() => setActiveFilter("all")}
                      className="ml-auto text-[10px] text-[#007A5F] font-bold hover:underline flex items-center gap-0.5"
                    >
                      Clear filter
                    </button>
                  )}
                </div>
              )} */}
            </div>

            {/* ── Filter Tabs ── */}
            <div
              className="flex items-center gap-1 px-3 py-2 border-b border-gray-100 dark:border-gray-800/80 overflow-x-auto shrink-0"
              style={{ scrollbarWidth: "none" }}
            >
              {FILTER_TABS.map((tab) => {
                const isActive = activeFilter === tab.key;
                const cfg = tab.key !== "all" ? CATEGORY_CONFIG[tab.key as NotificationCategory] : null;
                return (
                  <button
                    key={tab.key}
                    onClick={() => setActiveFilter(tab.key)}
                    className={`
                      relative flex items-center gap-1.5 px-3 py-1.5
                      text-[11px] font-semibold rounded-xl whitespace-nowrap
                      transition-all duration-200 shrink-0
                      ${isActive
                        ? cfg
                          ? `bg-gradient-to-br ${cfg.gradient} text-white shadow-sm`
                          : "bg-gradient-to-br from-[#007A5F] to-[#005a46] text-white shadow-sm"
                        : "text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800"
                      }
                    `}
                  >
                    {tab.label}
                  </button>
                );
              })}
            </div>

            {/* ── Notification List ── */}
            <div
              ref={scrollRef}
              onScroll={handleScroll}
              className="flex-1 overflow-y-auto"
              style={{
                maxHeight: "calc(100vh - 220px)",
                scrollbarWidth: "thin",
                scrollbarColor: "#e5e7eb transparent",
              }}
            >
              {loading ? (
                <div className="flex flex-col items-center justify-center py-16 gap-3">
                  <div className="relative">
                    <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-[#007A5F]/20 to-[#007A5F]/5 flex items-center justify-center">
                      <Loader2 size={18} className="animate-spin text-[#007A5F]" />
                    </div>
                  </div>
                  <p className="text-[12px] text-gray-400 font-medium">Loading activity...</p>
                </div>
              ) : notifications.length === 0 ? (
                <EmptyState
                  onReset={() => setActiveFilter("all")}
                  showReset={activeFilter !== "all"}
                />
              ) : (
                <>
                  {notifications.map((n, i) => (
                    <NotificationCard key={`${n.category}-${n._id}`} notification={n} index={i} />
                  ))}
                  {loadingMore && (
                    <div className="py-4 flex justify-center">
                      <Loader2 size={16} className="animate-spin text-gray-300 dark:text-gray-600" />
                    </div>
                  )}
                  {!hasMore && notifications.length > 0 && (
                    <div className="py-5 flex flex-col items-center gap-1">
                      <div className="w-8 h-[1px] bg-gradient-to-r from-transparent via-gray-300 dark:via-gray-700 to-transparent" />
                      <p className="text-[10px] text-gray-400 dark:text-gray-600 font-medium py-1">
                        You're all caught up
                      </p>
                      <div className="w-8 h-[1px] bg-gradient-to-r from-transparent via-gray-300 dark:via-gray-700 to-transparent" />
                    </div>
                  )}
                </>
              )}
            </div>

            {/* ── Footer ── */}
            <div className="px-4 py-2.5 border-t border-gray-100 dark:border-gray-800/80 shrink-0 flex items-center justify-between">
              <p className="text-[10px] text-gray-400 dark:text-gray-600">
                {notifications.length} loaded · auto-refreshes every min
              </p>
              <div className="flex items-center gap-1">
                <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                <span className="text-[10px] text-emerald-500 dark:text-emerald-400 font-semibold">Live</span>
              </div>
            </div>
          </div>,
          document.body
        )}
    </>
  );
}